import type { IntakeSubmission, MessageKey } from '@itin/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Button, ErrorNotice } from '../components/ui.js'
import { api } from '../lib/api.js'
import { useT } from '../lib/i18n.js'

/**
 * Ana's side of the intake form: the link to send, and what came back.
 *
 * Everything shown here was typed by a stranger, so it is rendered as text and
 * never as markup, and the WhatsApp link is built from the digits the server
 * already normalised.
 */
export function IntakeInboxPage() {
  const t = useT()
  const queryClient = useQueryClient()
  const session = useQuery({ queryKey: ['session'], queryFn: api.me })
  const list = useQuery({ queryKey: ['intake'], queryFn: api.listIntake })
  const [copied, setCopied] = useState(false)

  const toggle = useMutation({
    mutationFn: (s: IntakeSubmission) => api.markIntakeHandled(s.id, s.handledAt === null),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['intake'] }),
  })

  const link = session.data ? `${window.location.origin}/intake/${session.data.user.agencyId}` : ''

  async function copy() {
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard can be refused; the link is visible and selectable below.
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-col gap-1">
        <h1 className="font-display text-2xl font-semibold">{t('intake.inbox.title')}</h1>
        <p className="text-sm text-slate-500">{t('intake.inbox.hint')}</p>
      </header>

      {link && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-slate-700">{t('intake.inbox.linkLabel')}</span>
            <code
              data-testid="intake-link"
              className="break-all rounded bg-slate-100 px-2 py-1.5 text-xs text-slate-700"
            >
              {link}
            </code>
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" onClick={copy}>
                {copied ? t('intake.inbox.copied') : t('intake.inbox.copy')}
              </Button>
              <a
                href={link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex min-h-11 items-center px-2 text-sm text-slate-600 underline-offset-4 hover:underline sm:min-h-0"
              >
                {t('intake.inbox.open')}
              </a>
            </div>
          </div>
        </div>
      )}

      {list.isError && <ErrorNotice error={list.error} />}
      {list.data?.length === 0 && (
        <p data-testid="intake-empty" className="text-sm text-slate-500">
          {t('intake.inbox.empty')}
        </p>
      )}

      <ul className="flex flex-col gap-3">
        {list.data?.map((s) => (
          <li key={s.id} data-testid="intake-row">
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex flex-col gap-2 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-display text-lg font-semibold">{s.name}</p>
                    <p className="text-slate-600">
                      {s.destination} · {s.whenText}
                      {s.durationDays ? ` · ${s.durationDays}d` : ''}
                    </p>
                  </div>
                  {s.handledAt === null && (
                    <span className="rounded-full bg-[#fdf6e3] px-2 py-0.5 text-xs text-slate-700">
                      {t('intake.inbox.new')}
                    </span>
                  )}
                </div>
                <p className="text-slate-600">
                  {t('intake.inbox.travellers', { adults: s.adults, children: s.children })}
                  {s.childrenAges ? ` (${s.childrenAges})` : ''}
                </p>
                <p className="text-slate-700">
                  {t(`intake.comfort.${s.comfort}.title` as MessageKey)} ·{' '}
                  {t(`intake.band.${s.budgetBand}` as MessageKey)}
                </p>
                {s.interests.length > 0 && (
                  <p className="text-slate-500">
                    {s.interests.map((i) => t(`intake.interest.${i}` as MessageKey)).join(' · ')}
                  </p>
                )}
                {s.lastTrip && (
                  <p>
                    <span className="font-medium">{t('intake.inbox.lastTrip')}: </span>
                    {s.lastTrip}
                  </p>
                )}
                {s.notes && (
                  <p>
                    <span className="font-medium">{t('intake.inbox.notes')}: </span>
                    {s.notes}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <a
                    href={`https://wa.me/${waDigits(s.whatsapp)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex min-h-11 items-center text-slate-700 underline underline-offset-4 sm:min-h-0"
                  >
                    {t('intake.inbox.whatsapp')}
                  </a>
                  <Button
                    variant="quiet"
                    disabled={toggle.isPending}
                    onClick={() => toggle.mutate(s)}
                  >
                    {s.handledAt === null ? t('intake.inbox.markHandled') : t('intake.inbox.reopen')}
                  </Button>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** A number typed without a country code is Brazilian: the form is pt-BR. */
function waDigits(whatsapp: string): string {
  const digits = whatsapp.replace(/\D/g, '')
  return whatsapp.startsWith('+') || digits.length > 11 ? digits : `55${digits}`
}
